import React from "react";
import { GridEventListener } from "@mui/x-data-grid-pro";
import { PermissionDto, RoleDto, RoleFolderPermissionDto } from "../types/Role";
import { getCurrentESTTime } from "./dateHelper";
import { AuditNavigationTrace } from "../types/AuditNavigationTrace";
import { GetAllFilesAndFoldersByUserDto } from "../types/FolderPermission";
import { FileActions, FolderFileType } from "../data/Enums";
import { GLOBALMESSAGES } from "../constants/messages";
import {
  AUDITNAVIGATIONTRACES,
  GLOBALSETTINGS,
  IMPERSONATION_INFO,
  LeftSideNavModuleSet,
  LOCALSTORAGENAME,
  ModuleName,
  ModuleNames,
  PERMISSION_KEYS,
  permissionMap,
  permissionModuleMap,
  RouteKeywords,
  TOKEN,
  USERINFO,
} from "../data/constants";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { AuthTokenDto } from "../types/User";
import type { JSX } from "react";
import { MenuSection } from "../types/Menu";
import { UserModuleRolePermission } from "../types/UserModuleRolePermission";
import { PortalFileDto, PortalFolderDto } from "../types/Folder";

export const mapRolesToPermissions = (
  roles: RoleDto[],
  currentUserId: number,
  isEdit?: boolean
): RoleFolderPermissionDto[] => {
  const currentESTTime = getCurrentESTTime();

  const sortedRoles = [...roles].sort((a, b) => {
    const aHasUser = a.users?.some((user) => user.userId === currentUserId) ? 1 : 0;
    const bHasUser = b.users?.some((user) => user.userId === currentUserId) ? 1 : 0;
    return bHasUser - aHasUser;
  });

  return sortedRoles.map((role) => {
    const hasCurrentUser = role.users?.some((user) => user.userId === currentUserId) ?? false;
    const userPermission = hasCurrentUser && !isEdit;

    return {
      roleId: role.roleId,
      roleName: role.roleName,
      activeFrom: currentESTTime,
      activeUntil: null,
      canView: userPermission,
      canDownload: userPermission,
      canShareToUser: userPermission,
      canShareToRole: userPermission,
      canEdit: userPermission,
      canDelete: userPermission,
      canManagePermissions: userPermission,
      users:
        role.users?.map((user) => ({
          userId: user.userId,
          userName: user.userName,
          isChecked: true,
        })) || [],
    };
  });
};
export const mapDefaultPermissions = (
  rolePermissions: RoleFolderPermissionDto[],
  currentUserId: number
): RoleFolderPermissionDto[] => {
  return rolePermissions.map((role) => {
    const hasCurrentUser = role.users?.some((user) => user.userId === currentUserId) ?? false;

    return {
      ...role,
      canView: hasCurrentUser ? true : role.canView,
      canDownload: hasCurrentUser ? true : role.canDownload,
      canShareToUser: hasCurrentUser ? true : role.canShareToUser,
      canShareToRole: hasCurrentUser ? true : role.canShareToRole,
      canEdit: hasCurrentUser ? true : role.canEdit,
      canDelete: hasCurrentUser ? true : role.canDelete,
      canManagePermissions: hasCurrentUser ? true : role.canManagePermissions,
    };
  });
};

export const shouldShowMessage = (
  rolePermissions: RoleFolderPermissionDto[],
  currentUserId: number
): boolean => {
  const showMessage = rolePermissions.some((role) => {
    const hasCurrentUser = role.users?.some((user) => user.userId === currentUserId);

    if (!hasCurrentUser) return false;

    // Return true if ANY of the permissions is false
    return (
      !role.canView ||
      !role.canDownload ||
      !role.canShareToUser ||
      !role.canShareToRole ||
      !role.canEdit ||
      !role.canDelete ||
      !role.canManagePermissions
    );
  });

  return showMessage;
};

export const getFilteredActionsLength = (row: GetAllFilesAndFoldersByUserDto) => {
  return Object.keys(FileActions).filter((key) => {
    const permission = permissionMap[key];
    if (key === FileActions.Publish) {
      return row.isPublish === false;
    } else if (key === FileActions.UndoPublish) {
      return row.isPublish === true && row[permissionMap[FileActions.Edit]];
    } else if (key === FileActions.AddNew && row.type === FolderFileType[FolderFileType.File]) {
      return false;
    } else {
      return row[permission] === true;
    }
  }).length;
};

export function areAllRowsReadOnly(rows: GetAllFilesAndFoldersByUserDto[]): boolean {
  return rows.every((row) => getFilteredActionsLength(row) === 0);
}

export const getUncheckedUsers = (rolePermissions: RoleFolderPermissionDto[]) => {
  const uncheckedUsers = rolePermissions.flatMap((role) =>
    role.users
      .filter((user) => !user.isChecked)
      .map((user) => ({
        roleId: role.roleId,
        userId: user.userId,
      }))
  );
  return uncheckedUsers;
};

export const handleScrollPositionChange: GridEventListener<"scrollPositionChange"> = () => {
  // intentionally left empty
};

export const getSavedRecentItems = (
  fileDetails: GetAllFilesAndFoldersByUserDto[],
  userId: number,
  type: FolderFileType
) => {
  const logs: AuditNavigationTrace[] = fileDetails.map((item) => ({
    userId: userId,
    objectType: item.type,
    objectId: item.type === FolderFileType[FolderFileType.File] ? item.fileId : item.folderId,
    objectName: item.description ?? "",
    action: GLOBALMESSAGES.Retrieved,
    navigationDateTime: getCurrentESTTime(),
  }));
  let recentType = AUDITNAVIGATIONTRACES.RECENT_VIEWED_FOLDERS;
  if (type === FolderFileType.File) {
    recentType = AUDITNAVIGATIONTRACES.RECENT_VIEWED_FILES;
  } else {
    recentType = AUDITNAVIGATIONTRACES.RECENT_VIEWED_FOLDERS;
  }
  localStorage.setItem(recentType, JSON.stringify(logs));
};

export const renderSvgIcon = (type: string, className = "", fontSize = 20) => {
  type = type?.toLowerCase() ?? "default";

  const icons: { [key: string]: JSX.Element } = {
    folder: <img src="/folder.svg" alt="Folder Icon" className={className} />,

    // Image file types
    jpg: <img src="/image.svg" alt="JPG file" className={className} />,
    jpeg: <img src="/image.svg" alt="JPEG file" className={className} />,
    png: <img src="/image.svg" alt="PNG file" className={className} />,
    gif: <img src="/image.svg" alt="GIF file" className={className} />,
    svg: <img src="/image.svg" alt="SVG file" className={className} />,

    // Document file types
    pdf: <PictureAsPdfIcon sx={{ color: "red", fontSize }} className={className} />,
    txt: <DescriptionIcon sx={{ color: "#1976d2", fontSize }} className={className} />,
    doc: <img src="/word.svg" alt="Word Icon" className={className} />,
    docx: <img src="/word.svg" alt="Word Icon" className={className} />,

    // Spreadsheet file types
    xls: <img src="/excel.svg" alt="Excel Icon" className={className} />,
    xlsx: <img src="/excel.svg" alt="Excel Icon" className={className} />,
    csv: <img src="/excel.svg" alt="CSV Icon" className={className} />,

    // Presentation file types
    ppt: <img src="/powerpoint.svg" alt="PowerPoint Icon" className={className} />,
    pptx: <img src="/powerpoint.svg" alt="PowerPoint Icon" className={className} />,

    // Video & Audio file types
    mp4: <img src="/video-doc.svg" alt="Video Icon" className={className} />,
    mp3: <img src="/audio.svg" alt="Audio Icon" className={className} />,
    wav: <img src="/audio.svg" alt="Audio Icon" className={className} />,

    // Default file type
    default: <InsertDriveFileIcon sx={{ fontSize }} className={className} />,
  };

  return icons[type] ?? icons["default"];
};

export const getFileExtension = (filename: string): string => {
  return filename?.includes(".") ? (filename?.split(".").pop() ?? "") : "";
};

export function filterDataBySearch<T>(data: T[], searchText: string): T[] {
  const lowerText = searchText.toLowerCase();

  return data.filter((item) =>
    Object.values(item ?? {}).some((value) => {
      if (!value) return false;

      const stringValue = value.toString().toLowerCase();

      const dateLike = /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}$/.test(stringValue);

      if (dateLike) {
        const date = new Date(stringValue);
        if (!isNaN(date.getTime())) {
          const formatted = date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          return formatted.toLowerCase().includes(lowerText);
        }
      }

      // Fallback: string contains
      return stringValue.includes(lowerText);
    })
  );
}
export function filterDataBySearchWithGridFields<T>(
  data: T[],
  searchText: string,
  searchableFields: string[]
): T[] {
  if (!searchText) return data;

  const rawSearch = searchText.trim().toLowerCase();

  return data.filter((item) =>
    searchableFields.some((field) => {
      const value = (item as string)?.[field];
      if (value === null) return false;

      const strValue = value.toString().toLowerCase();

      // Strict substring match
      return strValue.includes(rawSearch);
    })
  );
}
export function getModuleName(): ModuleName {
  const url = window.location.href.toLowerCase();
  for (const [key, keywords] of Object.entries(RouteKeywords)) {
    if (keywords.some((kw) => url.includes(kw))) {
      return ModuleNames[key as keyof typeof ModuleNames];
    }
  }

  return ModuleNames.FileExplorer;
}

export const setToken = (tokenData: AuthTokenDto) => {
  localStorage.setItem(TOKEN.ACCESSTOKEN, tokenData.accessToken);
  localStorage.setItem(TOKEN.REFRESHTOKEN, tokenData.refreshToken);
};

export const setOrRevokeImpersonation = (
  impersonationMessage: string | null,
  impersonationId: number | null,
  tokenData: AuthTokenDto,
  sessionValue: string,
  userId: number,
  userName: string,
  revoke: boolean,
  setUserId: (id: number) => void,
  setUserName: (name: string) => void,
  profile?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    email?: string | null;
    setFirstName: (name: string) => void;
    setLastName: (name: string) => void;
  }
) => {
  const isImpersonating = revoke ? "false" : "true";
  localStorage.setItem(IMPERSONATION_INFO.IsImpersonating, isImpersonating);
  localStorage.setItem(USERINFO.SESSIONVALUE, sessionValue);
  if (impersonationId) {
    localStorage.setItem(IMPERSONATION_INFO.ImpersonationId, impersonationId.toString());
  }
  if (impersonationMessage) {
    localStorage.setItem(IMPERSONATION_INFO.ImpersonatedMessage, impersonationMessage);
  }

  setToken(tokenData);
  setUserId(userId);
  setUserName(userName);
  if (profile?.firstName) {
    profile.setFirstName(profile.firstName);
  }
  if (profile?.lastName) {
    profile.setLastName(profile.lastName);
  }
  if (profile?.phone) {
    localStorage.setItem(LOCALSTORAGENAME.PHONENUMBER, profile.phone);
  }
  if (profile?.email) {
    localStorage.setItem(LOCALSTORAGENAME.EMAILADDRESS, profile.email);
  }
  if (revoke) {
    endImpersonation();
  }
};

export const endImpersonation = () => {
  localStorage.removeItem(IMPERSONATION_INFO.ImpersonationId);
  localStorage.removeItem(IMPERSONATION_INFO.Impersonator);
  localStorage.removeItem(IMPERSONATION_INFO.IsImpersonating);
  localStorage.removeItem(IMPERSONATION_INFO.ImpersonatedMessage);
};

export const getFilteredMenu = (allMenus: MenuSection[]) => {
  return allMenus
    .map((section) => ({
      ...section,
      listItems: section.listItems.filter((item) => LeftSideNavModuleSet.has(item.moduleName)),
    }))
    .filter((section) => section.listItems.length > 0);
};

export const isEmptyMessage = (html: string) => {
  const text = html.replace(/<[^>]*>/g, "").trim();
  return text.length === 0 || text === "&nbsp;";
};

export const playAlertSound = () => {
  const sound = new Audio("/media/Alert2.wav");
  sound.play().catch(console.error);
};

export const getDestinationUrl = (parentFolderId: number) => {
  let appUrl = window.location.origin;
  const parentId = parentFolderId <= 0 ? GLOBALSETTINGS.DEFAULT_STATE : parentFolderId;
  const destination =
    parentId === GLOBALSETTINGS.DEFAULT_STATE
      ? GLOBALSETTINGS.DEFAULT_PAGE
      : GLOBALSETTINGS.LIST_DETAIL_PAGE;
  appUrl = appUrl + destination;

  return appUrl;
};

export const setAccessToken = (token) => localStorage.setItem(TOKEN.ACCESSTOKEN, token);
export const getAccessToken = () => localStorage.getItem(TOKEN.ACCESSTOKEN);
export const getRefreshToken = () => localStorage.getItem(TOKEN.REFRESHTOKEN);

export const isTokenValid = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const base64Payload = token.split(".")[1];
    if (!base64Payload) return false;

    const payload = JSON.parse(atob(base64Payload));

    if (!payload.exp) return false;

    return payload.exp * 1000 > Date.now();
  } catch {
    return false; // invalid token format
  }
};

export const getModulePermission = (
  action: keyof typeof FileActions,
  moduleName: string,
  permissions: UserModuleRolePermission[]
): boolean => {
  const permissionKey = permissionModuleMap[action];
  if (!permissionKey) return false;

  const modulePermission = permissions?.find((permission) => permission.moduleName === moduleName);
  return modulePermission?.[permissionKey] === true;
};

export const getInitials = (name: string | null | undefined): string => {
  if (!name) return "";

  return name
    .split(" ")
    .filter((n) => n)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

/** Username when present; otherwise First + Last. Used when Username is unavailable (Bug 79974). */
export const getUserDisplayName = (
  userName?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string => {
  const trimmedUserName = userName?.trim();
  if (trimmedUserName) {
    return trimmedUserName;
  }
  return `${firstName ?? ""} ${lastName ?? ""}`.trim();
};

/**
 * Whether the HR employee photo should stand in for a missing portal profile photo (Bug 79975).
 * The API distinguishes two "no portal photo" states: `null` means the user never set one, so the
 * HR photo is a sensible default; an empty string means the user explicitly removed their photo and
 * must fall through to the initials avatar instead.
 */
export const shouldUseEmployeePhotoFallback = (profileImage?: string | null): boolean =>
  profileImage === null || profileImage === undefined;

export const getUserPermissions = (
  currentRow: PortalFolderDto | PortalFileDto | null,
  userId: number
): PermissionDto => {
  const roles = currentRow?.roleFolderPermissions ?? [];

  // roles that include the user
  const currentUserRoles = roles?.filter((role) => role.users?.some((u) => u.userId === userId));

  // restricted roleIds for this user
  const restrictedSet = new Set(
    (currentRow?.restrictedUsers ?? []).filter((ru) => ru.userId === userId).map((ru) => ru.roleId)
  );

  // start with all-false
  const result: PermissionDto = {
    canView: false,
    canDownload: false,
    canShareToUser: false,
    canShareToRole: false,
    canEdit: false,
    canDelete: false,
    canManagePermissions: false,
  };

  // OR permissions across non-restricted roles
  for (const role of currentUserRoles) {
    if (restrictedSet.has(role.roleId)) continue; // treat this role as all-false
    for (const key of PERMISSION_KEYS) {
      result[key] = result[key] || !!role[key];
    }
  }

  return result;
};
